import { ComponentProps, useEffect, useRef } from "react";
import Markdown from "react-markdown";
import type { Components } from "react-markdown";
import styled from "@emotion/styled";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { Spacing, SpacingProps } from "@unlikelyai-magic/ui/layouts";
import { useChatMessageState } from "../hooks";
import { Message, MessageAuthor } from "../types";
import { BotAvatar } from "./BotAvatar";
import { ChatBubble } from "./ChatBubble";
import { ChatTyping } from "./ChatTyping";
import { UserAvatar } from "./UserAvatar";

// Prevent isHighlighted from being forwarded to DOM elements
const AvatarContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "isHighlighted",
})<{ isHighlighted?: boolean }>`
  padding-top: 0.25rem;
  transition: transform 0.3s ease-in-out;
`;

const BotAvatarContainer = styled(AvatarContainer, {
  shouldForwardProp: (prop) => prop !== "isHighlighted",
})`
  transform: ${({ isHighlighted }) =>
    isHighlighted ? "scale(1.2) translateX(10%)" : "scale(1) translateX(0)"};
`;

const UserAvatarContainer = styled(AvatarContainer, {
  shouldForwardProp: (prop) => prop !== "isHighlighted",
})`
  transform: ${({ isHighlighted }) =>
    isHighlighted ? "scale(1.2) translateX(-10%)" : "scale(1) translateX(0)"};
`;

const ChatWidgetContainer = styled.div<{ message: Message }>`
  display: flex;
  flex-direction: row;
  padding-top: 1rem;
  padding-left: 3.25rem;
  justify-content: center;
`;

const MarkdownContainer = styled.div`
  p {
    margin: 0;
    padding: 0;
  }
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: ${({ theme }) => theme.lineHeights.md};
`;

// Custom components for Markdown rendering
const markdownComponents: Components = {
  a: (props: { href?: string; children?: React.ReactNode }) => (
    <a href={props.href} target="_blank" rel="noopener noreferrer">
      {props.children}
    </a>
  ),
};

type MessageContainerProps = SpacingProps & {
  author: MessageAuthor;
  previousAuthor: MessageAuthor;
  isHighlighted?: boolean;
};

export const MessageContainer = styled(
  ({ author, previousAuthor, ...spacingProps }: MessageContainerProps) => (
    <Spacing {...spacingProps} />
  ),
  {
    shouldForwardProp: (prop) =>
      prop !== "author" &&
      prop !== "previousAuthor" &&
      prop !== "isHighlighted",
  }
)`
  margin-top: ${({ author, previousAuthor }) =>
    author === previousAuthor ? "0" : "0.25rem"};
  gap: 0;
`;

type ChatMessageProps = ComponentProps<"div"> & {
  message: Message;
  isHighlighted: boolean;
};

export const ChatMessage = styled(
  ({ message, isHighlighted, ...divProps }: ChatMessageProps) => {
    const messageEndRef = useRef<null | HTMLDivElement>(null);
    const messageBeginningRef = useRef<null | HTMLDivElement>(null);

    const { messages, latestBotMessageId } = useChatMessageState();
    const nextMessageAuthor = messages[messages.indexOf(message) + 1]?.author;

    const previousMessage = messages[messages.indexOf(message) - 1];
    const previousMessageAuthor = previousMessage?.author;

    const lastBotMessageText = messages.find(
      (msg) => msg.id === latestBotMessageId
    )?.text;

    useEffect(() => {
      const scrollToBottom = () =>
        messageEndRef.current?.scrollIntoView({ behavior: "smooth" });

      // Slight delay to wait for all components to render and animate before scrolling to the bottom
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(scrollToBottom);
      }, 100);

      return () => clearTimeout(timeoutId);
    }, [messages.length, lastBotMessageText]);

    useEffect(() => {
      if (isHighlighted) {
        messageBeginningRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, [isHighlighted, message.text]);

    const showTyping = !message.text && message.author === MessageAuthor.Bot;

    return (
      <MessageContainer
        author={message.author}
        previousAuthor={previousMessageAuthor}
        isHighlighted={isHighlighted}
      >
        <div {...divProps} ref={messageBeginningRef}>
          {message.author === MessageAuthor.Bot && (
            <BotAvatarContainer isHighlighted={isHighlighted}>
              <BotAvatar
                data-testid="bot-avatar"
                // only show the avatar on the last message in a chain of messages from the same author
                visible={nextMessageAuthor !== MessageAuthor.Bot}
              />
            </BotAvatarContainer>
          )}
          <ChatBubble author={message.author} isHighlighted={isHighlighted}>
            {showTyping ? (
              <ChatTyping />
            ) : typeof message.text === "string" ? (
              <MarkdownContainer>
                <Markdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {message.text}
                </Markdown>
              </MarkdownContainer>
            ) : (
              message.text
            )}
          </ChatBubble>
          {message.author === MessageAuthor.User && (
            <UserAvatarContainer isHighlighted={isHighlighted}>
              <UserAvatar
                data-testid="bot-avatar"
                // only show the avatar on the last message in a chain of messages from the same author
                visible={nextMessageAuthor !== MessageAuthor.User}
              />
            </UserAvatarContainer>
          )}
        </div>
        {message.widget && (
          <ChatWidgetContainer message={message}>
            {message.widget}
          </ChatWidgetContainer>
        )}
        <div ref={messageEndRef} />
      </MessageContainer>
    );
  }
)`
  display: flex;
  align-items: flex-start;
  justify-content: ${({ message }) =>
    message.author === MessageAuthor.Bot ? "flex-start" : "flex-end"};
  gap: 0.5rem;
`;
